import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Shield } from 'lucide-react';

const TermsAndConditions = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FAFAFA 0%, #F0F9F8 100%)' }}>
      <div className="max-w-4xl mx-auto p-4 md:p-8">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full hover:bg-gray-100"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-3xl font-bold" style={{ color: '#333' }}>Terms and Conditions</h1>
            <p className="text-gray-500 text-sm">Last updated: December 2025</p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="card prose prose-sm max-w-none"
        >
          <div className="flex items-center gap-2 mb-6 p-4 rounded-xl" style={{ backgroundColor: '#f0f9f8' }}>
            <Shield size={24} style={{ color: '#A2DAD3' }} />
            <p className="text-sm m-0">Please read these terms carefully before using Annie Cares.</p>
          </div>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>1. Acceptance of Terms</h2>
          <p className="text-gray-700 mb-4">
            By accessing or using Annie Cares ("the Service"), you agree to be bound by these Terms and Conditions. 
            If you do not agree to these terms, please do not use the Service.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>2. Description of Service</h2>
          <p className="text-gray-700 mb-4">
            Annie Cares is a health tracking application designed to help caregivers monitor and record health 
            information for their patients or loved ones. The Service allows users to:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Track symptoms, medications, and health metrics</li>
            <li>Record blood pressure and blood sugar readings</li>
            <li>Log appointments and critical health events</li>
            <li>Generate health reports for medical professionals</li>
            <li>Share patient information with authorized caregivers</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>3. Medical Disclaimer</h2>
          <div className="p-4 rounded-xl mb-4" style={{ backgroundColor: '#fff5f5', border: '1px solid #F9A7A7' }}>
            <p className="text-gray-700 m-0">
              <strong>Important:</strong> Annie Cares is NOT a medical device and is not intended to diagnose, treat, 
              cure, or prevent any disease or health condition. The information provided through the Service is for 
              informational purposes only and should not replace professional medical advice, diagnosis, or treatment. 
              Always seek the advice of your physician or other qualified health provider with any questions you may 
              have regarding a medical condition.
            </p>
          </div>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>4. User Accounts</h2>
          <p className="text-gray-700 mb-4">
            To use certain features of the Service, you must create an account. You are responsible for:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Maintaining the confidentiality of your account credentials</li>
            <li>All activities that occur under your account</li>
            <li>Ensuring that all information you provide is accurate and current</li>
            <li>Notifying us immediately of any unauthorized use of your account</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>5. Subscription and Payments</h2>
          <p className="text-gray-700 mb-4">
            Annie Cares offers both free and premium subscription plans:
          </p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li><strong>Free Plan:</strong> Limited to one patient profile with basic tracking features</li>
            <li><strong>Premium Plan:</strong> Unlimited patients, appointments, food restrictions, and advanced features</li>
            <li>New users receive a 14-day free trial of premium features</li>
            <li>Subscription fees are billed monthly ($5/month) or annually ($39/year)</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>6. User Responsibilities</h2>
          <p className="text-gray-700 mb-4">You agree not to:</p>
          <ul className="list-disc pl-6 text-gray-700 mb-4 space-y-1">
            <li>Use the Service for any unlawful purpose</li>
            <li>Share your account with unauthorized individuals</li>
            <li>Enter false or misleading health information</li>
            <li>Attempt to access other users' data without authorization</li>
            <li>Interfere with or disrupt the Service or its servers</li>
          </ul>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>7. Data and Privacy</h2>
          <p className="text-gray-700 mb-4">
            Your privacy is important to us. Please review our <a href="/privacy" style={{ color: '#A2DAD3' }}>Privacy Policy</a> to 
            understand how we collect, use, and protect your personal and health information.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>8. Intellectual Property</h2>
          <p className="text-gray-700 mb-4">
            The Service and its original content, features, and functionality are owned by Annie Cares and are 
            protected by international copyright, trademark, and other intellectual property laws.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>9. Limitation of Liability</h2>
          <p className="text-gray-700 mb-4">
            To the maximum extent permitted by law, Annie Cares shall not be liable for any indirect, incidental, 
            special, consequential, or punitive damages, including but not limited to loss of data, health 
            complications, or other intangible losses resulting from your use of the Service.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>10. Termination</h2>
          <p className="text-gray-700 mb-4">
            We may terminate or suspend your account and access to the Service immediately, without prior notice, 
            for conduct that we believe violates these Terms or is harmful to other users, us, or third parties, 
            or for any other reason at our sole discretion.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>11. Changes to Terms</h2>
          <p className="text-gray-700 mb-4">
            We reserve the right to modify these Terms at any time. We will notify users of any material changes 
            by posting the new Terms on this page and updating the "Last updated" date. Your continued use of the 
            Service after any changes constitutes acceptance of the new Terms.
          </p>

          <h2 className="text-xl font-semibold mt-6 mb-3" style={{ color: '#A2DAD3' }}>12. Contact Us</h2>
          <p className="text-gray-700 mb-4">
            If you have any questions about these Terms, please contact us through the app or reach out to our 
            support team.
          </p>

          <div className="mt-8 pt-6 border-t text-center">
            <button
              onClick={() => navigate(-1)}
              className="btn btn-primary px-8"
            >
              I Understand
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
